// Pure selector for the MT5 dashboard account card.
// The UI may show a recent, last-confirmed DEMO snapshot during a short reconnect,
// but must hide it for wrong-account/non-demo states or after the hard age limit.

export const MT5_ACCOUNT_MAX_STALE_MS = 30 * 60 * 1000;

const BLOCKED_CODES = new Set(["wrong_account", "not_demo"]);
const BLOCKED_STATUSES = new Set(["blocked_wrong_account", "blocked_not_demo"]);

function usableAccount(account) {
  return Boolean(account && typeof account === "object" && account.login != null);
}

function toEpochMs(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1e12 ? n : n * 1000;
}

export function selectMt5AccountView({
  liveAccount,
  liveError,
  liveConfirmedAt,
  forwardStatus,
  now = Date.now(),
  maxStaleMs = MT5_ACCOUNT_MAX_STALE_MS,
} = {}) {
  const forwardCode = BLOCKED_STATUSES.has(forwardStatus?.status)
    ? forwardStatus.status.replace(/^blocked_/, "")
    : null;
  const blockedCode = BLOCKED_CODES.has(liveError?.code) ? liveError.code : forwardCode;

  if (blockedCode) {
    return {
      account: null,
      mode: blockedCode,
      blocked: true,
      stale: false,
      ageMs: null,
      confirmedAt: null,
    };
  }

  if (usableAccount(liveAccount) && !liveError) {
    const confirmedAt = toEpochMs(liveConfirmedAt) || now;
    return {
      account: liveAccount,
      mode: "live",
      blocked: false,
      stale: false,
      ageMs: Math.max(0, now - confirmedAt),
      confirmedAt,
    };
  }

  const cachedAccount = usableAccount(liveAccount)
    ? liveAccount
    : usableAccount(forwardStatus?.account)
      ? forwardStatus.account
      : null;
  const confirmedAt = usableAccount(liveAccount)
    ? toEpochMs(liveConfirmedAt)
    : toEpochMs(forwardStatus?.last_tick);
  const ageMs = confirmedAt ? Math.max(0, now - confirmedAt) : null;

  if (cachedAccount && ageMs != null && ageMs <= maxStaleMs) {
    return {
      account: cachedAccount,
      mode: "reconnecting",
      blocked: false,
      stale: true,
      ageMs,
      confirmedAt,
    };
  }

  return {
    account: null,
    mode: cachedAccount ? "stale_expired" : "waiting_for_first_tick",
    blocked: false,
    stale: false,
    ageMs,
    confirmedAt: confirmedAt || null,
  };
}

export function normalizeMt5Bars(bars) {
  return (Array.isArray(bars) ? bars : []).map(b => Array.isArray(b)
    ? Object.fromEntries(["time", "open", "high", "low", "close", "tick_volume", "spread", "real_volume"].map((k, i) => [k, b[i]])) : b)
    .filter(b => b && Number.isFinite(Number(b.time)) && Number(b.time) > 0)
    .sort((a, b) => a.time - b.time);
}

export function mt5BarFreshness({ bar, timeframe, brokerOffsetSec, now = Date.now() } = {}) {
  const seconds = { M1: 60, M5: 300, M15: 900, M30: 1800, H1: 3600, H4: 14400, D1: 86400 }[timeframe];
  if (!bar || !seconds || !Number.isFinite(brokerOffsetSec)) return { live: false, label: "STALE", timeUtc: null };
  const timeUtc = Number(bar.time) - brokerOffsetSec;
  const age = now / 1000 - timeUtc;
  const weekend = [0, 6].includes(new Date(now).getUTCDay());
  // Weekend day bars must not make Friday data look live for another 24 hours.
  const live = age >= -60 && age <= (weekend ? Math.min(seconds + 120, 300) : seconds + 120);
  return { live, label: live ? "LIVE" : weekend && age > 300 ? "WEEKEND HALT / STALE" : "STALE", timeUtc, ageSec: age };
}
